import store from '@/store/store.js'
import axios from '@/utils/axios'
import Smooch from 'smooch'
import { getAbsoluteBackendURL } from '@/utils/backend'
import i18n from '@/i18n/i18n'
import serializeUrlParams from '@/utils/serialize-url-params.js'
import FileUploadService from '@/services/file-upload.service.js'

/** @typedef {import('@/types/support').SubbrokerCarrierSchema} SubbrokerCarrierSchema */
/** @typedef {import('@/types/support').DSFProofOfDeliveryResponse} DSFProofOfDeliveryResponse */
/** @typedef {import('@/types/support').DSFSchema} DSFSchema */
/** @typedef {import('@/types/support').SupportRequestPayload} SupportRequestPayload */
/** @typedef {import('@/types/support').ClaimQueryResponse} ClaimQueryResponse */
/** @typedef {import('@/types/support').EspertoClaimResponse} EspertoClaimResponse */
/** @typedef {import('@/types/support').SupportPaginatedResponse} SupportPaginatedResponse */
/** @typedef {import('@/types/support').SupportChatMetafields} SupportChatMetafields */
/** @typedef {import('smooch').Conversation} SmoochConversation */
/** @typedef {import('smooch').Message} SmoochMessage */

const COLOR_SENDCLOUD_DARK_BLUE = '112857'
const COLOR_SENDCLOUD_LIGHT_BLUE = '1d97ff'

const axiosDSFClient = axios.create({
  baseURL: getAbsoluteBackendURL(import.meta.env.VUE_APP_SUPPORT_DSF_BASE_URL),
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
})

const axiosClient = axios.create({
  baseURL: getAbsoluteBackendURL(import.meta.env.VUE_APP_SUPPORT_SERVICE_BASE_URL),
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
})

/**
 * Builds the DSF Service URL for the route passed.
 * @param {string} route 'i.e /dsf/carrier-activation'.
 * @returns {string} a URL pointing to the DSF service.
 */
function buildDSFUrl(route) {
  const baseUrl = getAbsoluteBackendURL(import.meta.env.VUE_APP_SUPPORT_DSF_BASE_URL)
  return `${baseUrl}${route}`
}

/**
 * Builds the Support Service URL for the route passed.
 * @param {string} route 'i.e /refund-claim'.
 * @returns {string} a URL pointing to the Support service.
 */
function buildSupportUrl(route) {
  const baseUrl = getAbsoluteBackendURL(import.meta.env.VUE_APP_SUPPORT_SERVICE_BASE_URL)
  return `${baseUrl}${route}`
}

/**
 * Check if the Ada widget button is there
 * @returns {Element | null}
 */
function getAdaButton() {
  if (window.adaEmbed) {
    return document.querySelector('.ada-button-frame')
  }
  return null
}

function setAdaSettings() {
  // Ada only supports languages in ISO-639-1 format, so only 2 letter country codes :(
  const language = i18n.locale.split('-')[0]

  // Create a flag to track if the callback has already run
  let scriptHasRun = false

  window.adaSettings = {
    language,
    crossWindowPersistence: true,
    conversationEndCallback: (_event) => {
      if (scriptHasRun) {
        return
      }
      scriptHasRun = true
      window.adaEmbed.toggle()
      setTimeout(() => {
        window.adaEmbed.triggerAnswer('64c2002eae079338feed3c26')
      }, 1000)
    },
  }
}

/**
 * Returns an object with the populated metafields for the widget
 * @returns {SupportChatMetafields}
 */
function getMetafields() {
  return {
    corporation_id: store.getters.user.corporation?.id,
    date_joined: store.getters.user.date_joined?.substring(0, 10), // date in YYYY-MM-DD format
    email: store.getters.user.corporation?.email,
    invoice_country: store.getters.user.corporation?.invoice_country_iso_2,
    name: store.getters.user.company_name,
    plan: store.getters.user.new_plan_group,
    user_email: store.getters.user.email,
    user_id: store.getters.user.id,
  }
}

function setAdaMetafields() {
  window.adaEmbed.setMetaFields(getMetafields())
}

async function setSmoochMetafields() {
  await Smooch.updateUser({
    metadata: getMetafields(),
  })
}

function removeEmptyKeys(obj) {
  const filteredObj = Object.fromEntries(
    Object.entries(obj).filter(([_key, value]) => value && value !== 'all'),
  )
  return filteredObj
}

async function createSmoochConversation() {
  if (Smooch.isOpened()) {
    const conversation = await Smooch.createConversation()
    await Smooch.loadConversation(conversation.id)
    sendSmoochHiddenMessage(conversation)
  }
}

/**
 * Sends a hidden message to trigger the widget autoreply
 * @param {Conversation} conversation
 */
function sendSmoochHiddenMessage(conversation) {
  Smooch.sendMessage({
    type: 'text',
    text: i18n.t('support.chat.hidden_hi_message'),
    metadata: {
      hidden: true,
    },
  },
  conversation.id,
  )
}

/**
 * Attachs an event listener to the new conversation button
 * @param {Document} iframeDocument
 */
function addConversationButtonListener(iframeDocument) {
  const conversationFooter = iframeDocument.getElementsByClassName('conversation-group-footer')[0]
  const newConversationButton = conversationFooter?.querySelector('button')
  if (newConversationButton) {
    newConversationButton.addEventListener('click', () => createSmoochConversation())
  }
}

/**
 * Overrides the syling removing the Powered by Zendesk logo
 * @param {HTMLElement} iframe
 */
function overrideSmoochStyles(iframe) {
  if (iframe.contentDocument && iframe.contentDocument.styleSheets) {
    const styleElement = iframe.contentDocument.createElement('style')
    const styles = `
      .messages-container .logo {
        display: none;
      }
      .footer-transition.hidden {
        display: none !important;
      }
    `
    styleElement.innerHTML = styles
    iframe.contentDocument.head.appendChild(styleElement)
  }
}

/**
 * Hides the chatbox footer so the user cannot type.
 * @param {boolean} shouldHide
 */
function toggleHideChatFooter(shouldHide = false) {
  const iframe = document.getElementById('web-messenger-container')
  const iframeDocument = iframe.contentDocument || iframe.contentWindow.document
  const footerElement = iframeDocument.querySelector('.footer-transition')

  if (!footerElement) {
    return
  }

  if (shouldHide) {
    footerElement.classList.add('hidden')
  } else {
    footerElement.classList.remove('hidden')
  }
}

export default {
  startAdaWidget() {
    if (!getAdaButton()) {
      setAdaSettings()

      const ADA_MESSAGING_BETA_FLAG = 'ada_messaging'
      const script = document.createElement('script')
      script.src = 'https://static.ada.support/embed2.js'
      script.id = '__ada'
      script.setAttribute(
        'data-handle',
        store.getters.hasBetaFeature(ADA_MESSAGING_BETA_FLAG) ? 'sendcloud-sandbox' : 'sendcloud',
      )
      document.body.appendChild(script)
      script.onload = () => {
        setAdaMetafields()
      }
    }
  },

  async startSmoochWidget() {
    Smooch.on('widget:opened', async function () {
      if (Smooch.getConversations().length === 0) {
        const newConversation = await Smooch.createConversation({
          metadata: {
            language: i18n.locale,
            webpage: window.location.href,
          },
        })
        sendSmoochHiddenMessage(newConversation)
      }
    })

    Smooch.on('message:received', async function (message, data) {
      const currentConversation = await Smooch.getConversationById(data.conversation.id)
      const displayedConversation = await Smooch.getDisplayedConversation()

      if (displayedConversation?.id === currentConversation?.id) {
        // we hide the footer with input and attach button if there are multiple actions or we get special metadata
        if (message.metadata?.hideFooter || message.actions?.length > 1) {
          toggleHideChatFooter(true)
          return
        }
        toggleHideChatFooter(false)
      }

      if (currentConversation.displayName !== message.displayName ||
        currentConversation.iconUrl !== message.avatarUrl) {
        await Smooch.updateConversation(data.conversation.id, {
          displayName: message.displayName,
          iconUrl: message.avatarUrl,
        })
      }
    })

    const jwtZendesk = await this.getZendeskToken()
    await Smooch.init({
      integrationId: '64da4e698cb265aea1149db7',
      externalId: String(store.getters.user?.corporation?.id),
      jwt: jwtZendesk,
      businessName: 'Sendcloud Messenger',
      locale: i18n.locale,
      businessIconUrl:
            'https://www.sendcloud.com/wp-content/help-center-images/Sendcloud%20brands/eyJwYXRoIjoic2VuZGNsb3VkXC9maWxlXC9GWHNaOTNYckJRV2lYNmJpN0dlUi5wbmcifQ_sendcloud_umGaaXQ-BT_JVn2SWSAXIIfZghsRqnSNj5KKiR4GWHM.png',
      customColors: {
        brandColor: COLOR_SENDCLOUD_DARK_BLUE,
        conversationColor: COLOR_SENDCLOUD_DARK_BLUE,
        actionColor: COLOR_SENDCLOUD_LIGHT_BLUE,
      },
      customText: {
        newConversationButtonText: i18n.t('support.chat.new_conversation'),
        conversationListHeaderText: i18n.t('support.chat.my_conversations'),
        inputPlaceholder: i18n.t('support.chat.type_a_message'),
      },
      menuItems: {
        imageUpload: true,
        fileUpload: true,
        shareLocation: false,
      },
      delegate: {
        beforeDisplay(message, _data) {
          // Hide hidden messages sent by the widget, i.e. the greeting message trigger
          if (message?.metadata?.hidden) {
            return null
          }
          return message
        },
      },
    })
    const iframe = document.getElementById('web-messenger-container')
    if (!iframe) {
      return
    }
    overrideSmoochStyles(iframe)
    const iframeDocument = iframe.contentDocument || iframe.contentWindow.document
    iframeDocument.addEventListener('click', () => addConversationButtonListener(iframeDocument), false)
    await setSmoochMetafields()
  },

  /**
   * Checks if the chat widget is enabled in the env variables
   * @returns {boolean}
   */
  isChatWidgetEnabled() {
    return import.meta.env.VUE_APP_CHAT_WIDGET_ENABLED === 'yes'
  },

  /**
   * Checks if the user is based in one of the rollout countries (temporary until full rollout)
   * @returns {boolean}
   */
  isCountryRolledOut() {
    const rolledOutCountries = ['ES', 'FR', 'GB', 'IT', 'DE', 'AT']
    return rolledOutCountries.includes(store.getters.user?.corporation?.invoice_country_iso_2)
  },

  /**
  * Checks if the user is part of the Smooch Beta
  * @returns {boolean}
  */
  isSmoochBetaUser() {
    const SMOOCH_CONVERSATIONS_BETA = 'smooch_conversations'
    return store.getters.hasBetaFeature(SMOOCH_CONVERSATIONS_BETA)
  },

  showAda() {
    const adaButton = getAdaButton()
    if (adaButton) {
      adaButton.style.display = 'block'
    }
  },

  hideAda() {
    const adaButton = getAdaButton()
    if (adaButton) {
      adaButton.style.display = 'none'
    }
  },

  hideSupportButtons() {
    if (this.isChatWidgetEnabled()) {
      /*
       * since the Ada widget is loaded asynchronously, we have to wait for it to load in
       * order to hide it. We check every 500ms and when it is found we hide it.
       */
      const adaElement = document.querySelector('#ada-button-frame')
      const adaLoaded = window.adaEmbed && typeof window.adaEmbed.stop === 'function'
      let timeOut
      if (adaLoaded && adaElement) {
        this.hideAda()
        if (timeOut) {
          clearInterval(timeOut)
        }
      } else {
        timeOut = setTimeout(this.hideSupportButtons.bind(this), 500)
      }
    }
  },

  async getZendeskToken() {
    const response = await axios.get('/xhr/support/zendesk/jwt-authentication')
    return response.data
  },

  /**
   * @param {String} carrierCode - for example 'dhl'
   * @returns {Promise<SubbrokerCarrierSchema>}
   */
  async getContractSubbrokerForm(carrierCode) {
    const endpoint = `/carrier-activation/${carrierCode}`
    const { data } = await axiosDSFClient.get(buildDSFUrl(endpoint))
    return data
  },

  /**
   * @param {String} carrierCode - for example 'dhl'
   * @param {Object} payload - the form data
   * @returns {Promise}
   */
  async sendSubbrokerContractRequest(carrierCode, payload) {
    const endpoint = `/carrier-activation/${carrierCode}/request`
    const { data } = await axiosDSFClient.post(buildDSFUrl(endpoint), payload)
    return data
  },

  /**
   * Request a proof of delivery document from the DSF service.
   *
   * @param {string} trackingNumber
   * @param {string} carrierCode
   * @returns {Promise<DSFProofOfDeliveryResponse>}
   */
  async getProofOfDelivery(trackingNumber, carrierCode) {
    const response = await axiosDSFClient.get(buildDSFUrl(`/proof-of-deliveries/${carrierCode}/${trackingNumber}`), {
      responseType: 'arraybuffer',
    })
    return response
  },

  /**
   * Retrieve a specific dynamic support form schema.
   *
   * @param {string} supportRequestType
   * @param {string} trackingNumber
   * @returns {Promise<DSFSchema>} the support form schema
   */
  async getSchema(supportRequestType, trackingNumber) {
    let url = buildDSFUrl(`/support-form-types/${supportRequestType}/fields`)

    if (trackingNumber) {
      url = `${url}?tracking_number=${trackingNumber}`
    }

    return await axiosDSFClient.get(url)
  },

  /**
   * Submit a dynamic support form to the DSF service.
   *
   * @param {string} supportRequestType
   * @param {SupportRequestPayload} supportRequestPayload
   * @returns {Promise}
   */
  async createTicketViaDSF(supportRequestType, supportRequestPayload) {
    const url = buildDSFUrl(`/support-form-types/${supportRequestType}/support-requests`)
    const response = await axiosDSFClient.post(url, supportRequestPayload)
    return response.data
  },

  /**
   * Fetch the support ticket categories from the DSF service.
   * @returns {Promise}
   */
  async getCategories() {
    const response = await axiosDSFClient.get(buildDSFUrl('/support-form-types/support-categories'))
    return response.data
  },

  async getTickets(params) {
    let url = buildDSFUrl('/support-form-types/tickets')

    if (Object.keys(params).length) {
      url = `${url}?${serializeUrlParams(params)}`
    }

    const { data } = await axiosDSFClient.get(url)
    return data
  },

  /**
   * Fetch support tickets by its type
   * @param {('shipment'|'platform')} ticketType - type of a support ticket
   * @param {Object} params - filters and page number of the query
   * @returns {Promise<SupportPaginatedResponse>}
   */
  async getTicketsByType(ticketType, params) {
    params = removeEmptyKeys(params)
    const url = buildDSFUrl(`/tickets/${ticketType}`)
    const { data } = await axiosDSFClient.get(url, { params })
    return data
  },

  /**
   * Marks a support ticket as solved
   * @param {number} ticketId
   * @returns {Promise}
   */
  async markTicketAsSolved(ticketId) {
    const url = buildDSFUrl(`/tickets/${ticketId}/solve`)
    const { data } = await axiosDSFClient.put(url)
    return data
  },

  /**
   * Retrieve ticket details
   *
   * @param {number} id
   * @returns {Promise}
   */
  async getTicket(id) {
    const { data } = await axiosDSFClient.get(buildDSFUrl(`/support-form-types/tickets/${id}`))
    return data
  },

  async addComment(id, comment, attachments) {
    const url = buildDSFUrl(`/support-form-types/tickets/${id}/comment`)
    const { data } = await axiosDSFClient.post(url, {
      id,
      comment,
      attachments,
    })
    return data
  },

  /**
   * Validate tracking number for the support category selected by user.
   *
   * @param {string} supportType
   * @param {string} trackingNumber
   * @returns {Promise}
   */
  async findParcel(supportType, trackingNumber) {
    const url = buildDSFUrl(`/support-form-types/tracking/parcel/${supportType}`)
    const { data } = await axiosDSFClient.post(url, {
      tracking_number: trackingNumber,
    })
    return data
  },

  /**
   * Uploads a file to DSF. If the url is not present, upload to the default path (SCP).
   *
   * @param {File} file
   * @param {string} url
   * @returns {Promise<{ data: { id: number, file: string, content_type: string }}>}
   * an object with a “file” field containing a URL to the uploaded file.
   */
  uploadToDSF(file, url) {
    if (!url) {
      return FileUploadService.upload(file)
    }
    const payload = new FormData()
    payload.append('file', file)

    return axiosDSFClient.post(`/support-form-types${url}`, payload)
  },

  /**
   * Defines if the user accepts immediately or waits for the refund claim
   *
   * @param {string} action
   * @param {string} payload
   * @returns {Promise<{data: ClaimQueryResponse}>}
   */
  manageRefundClaim(action, payload) {
    const url = buildSupportUrl(`/tickets/refund-claim/${action}`)
    return axiosClient.post(url, {
      enc_payload: payload,
    })
  },

  /**
   *
   * @param {string} payload
   * @returns {Promise<{data: EspertoClaimResponse}>}
   */
  getEspertoClaimInfo(payload) {
    const url = buildSupportUrl(`/tickets/esperto-claim`)
    return axiosClient.get(url, {
      params: {
        enc_payload: payload,
      },
    })
  },
}
